import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CommissionBreakdownModal from '@/components/CommissionBreakdownModal';
import { useI18n } from '@/lib/i18n';
import {
  Calculator, FileCheck2, Landmark, CheckCircle2, ArrowRight, ArrowLeft, Loader2,
  AlertCircle, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Extracted so React doesn't re-mount the sub-tree on every render.
const StepHeader = ({ n, total, icon: Icon, title, subtitle, language }) => {
  const es = language === 'es';
  return (
    <div className="flex gap-3 mb-4">
      <div className="shrink-0 h-10 w-10 rounded-full bg-[#F05D5E]/10 text-[#F05D5E] flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">
          {es ? `Paso ${n} de ${total}` : `Step ${n} of ${total}`}
        </p>
        <h3 className="text-base font-heading font-bold">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

/**
 * 3-step wizard that walks a business owner through everything required
 * to switch from "pay at location" to "online deposits":
 *   1) Show commission calculator + full breakdown (transparency)
 *   2) Capture CLABE (18-digit Mexican bank account) — skipped if already on file
 *   3) Accept the current commission-terms version (legal snapshot) — skipped if already accepted
 * Finally submits PATCH /businesses/me/payment-mode with everything at once.
 * If Stripe Connect isn't ready, backend returns 412 and we redirect to onboarding.
 *
 * Props:
 *   open, onOpenChange
 *   paymentModeState  — the current /businesses/me/payment-mode payload
 *   onActivated       — callback fired once the mode is successfully switched
 */
export default function ActivateDepositsWizard({ open, onOpenChange, paymentModeState, onActivated }) {
  const { language } = useI18n();
  const es = language === 'es';

  const [step, setStep] = useState(1);
  const [clabe, setClabe] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsPayload, setTermsPayload] = useState(null); // {version,hash,snapshot}
  const [submitting, setSubmitting] = useState(false);

  const alreadyHasClabe = !!paymentModeState?.has_clabe;
  const alreadyAcceptedTerms = !!paymentModeState?.commission_terms_accepted;

  // Reset internal state whenever the wizard is (re)opened.
  useEffect(() => {
    if (open) {
      setStep(1);
      setClabe('');
      setShowTermsModal(false);
      setTermsPayload(null);
      setSubmitting(false);
    }
  }, [open]);

  const goNext = () => setStep(s => s + 1);
  const goBack = () => setStep(s => Math.max(1, s - 1));

  // ─── Step handlers ────────────────────────────────────────────────────────
  const handleClabeContinue = () => {
    if (alreadyHasClabe) return goNext();
    const clean = (clabe || '').replace(/\D/g, '');
    if (clean.length !== 18) {
      toast.error(es ? 'La CLABE debe tener 18 dígitos' : 'CLABE must be 18 digits');
      return;
    }
    setClabe(clean);
    goNext();
  };

  const openTermsModal = () => setShowTermsModal(true);

  const handleTermsAccepted = (payload) => {
    setTermsPayload(payload);
    setShowTermsModal(false);
    // Small delay so the toast is visible before we submit
    toast.success(es ? 'Términos aceptados' : 'Terms accepted');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const body = { requires_deposit: true };
    if (!alreadyHasClabe && clabe) body.clabe = clabe;
    if (!alreadyAcceptedTerms && termsPayload) {
      body.commission_terms_accepted = true;
      body.commission_terms_version = termsPayload.version;
      body.commission_terms_hash = termsPayload.hash;
      body.commission_terms_snapshot = termsPayload.snapshot;
    }

    try {
      const res = await api.patch('/businesses/me/payment-mode', body);
      toast.success(es ? '¡Anticipos activados!' : 'Deposits activated!');
      onOpenChange(false);
      onActivated?.(res.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 412) {
        // Stripe Connect not ready — kick off onboarding
        toast.info(es
          ? 'Te llevaremos a completar tu cuenta Stripe Connect...'
          : "Taking you to complete your Stripe Connect account...");
        try {
          const onboard = await api.post('/stripe-connect/onboard');
          if (onboard.data?.url) {
            window.location.href = onboard.data.url;
            return;
          }
        } catch { /* fall through */ }
        toast.error(detail || (es ? 'Completa tu cuenta de Stripe Connect' : 'Complete your Stripe Connect account'));
      } else if (status === 429) {
        toast.error(detail || (es ? 'Espera el periodo de enfriamiento' : 'Wait for cooldown'));
      } else {
        toast.error(detail || (es ? 'No se pudo activar' : 'Could not activate'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Renderers ────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="activate-deposits-wizard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Sparkles className="h-5 w-5 text-[#F05D5E]" />
              {es ? 'Activar cobro de anticipos' : 'Enable online deposits'}
            </DialogTitle>
            <DialogDescription>
              {es
                ? '3 pasos rápidos para empezar a cobrar en línea de forma legal y transparente.'
                : '3 quick steps to start collecting online in a legal, transparent way.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1 — calculator + explanation */}
          {step === 1 && (
            <div>
              <StepHeader
                n={1} total={3}
                icon={Calculator}
                title={es ? 'Cómo funciona el cobro' : 'How the fee flow works'}
                subtitle={es ? 'Ve exactamente qué paga el cliente y qué recibes tú' : 'See exactly what the client pays and what you receive'}
                language={language}
              />
              <div className="space-y-3">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{es
                      ? 'El cliente paga un anticipo al reservar (protege tu tiempo).'
                      : 'The client pays a deposit at booking (protects your time).'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{es
                      ? 'Bookvia retiene el dinero de forma segura hasta el día 20 de cada mes.'
                      : 'Bookvia holds the funds safely until the 20th of each month.'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{es
                      ? 'Ese día se transfiere a tu cuenta Stripe → banco en 1-2 días hábiles.'
                      : 'On that day it transfers to your Stripe → bank in 1-2 business days.'}</span>
                  </li>
                </ul>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={openTermsModal}
                  data-testid="wizard-open-calculator-btn"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {es ? 'Ver desglose completo (calculadora)' : 'View full breakdown (calculator)'}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  {es
                    ? 'La calculadora te muestra qué paga el cliente, qué retiene Stripe, qué se queda Bookvia y cuánto recibes neto.'
                    : "The calculator shows what the client pays, what Stripe keeps, what Bookvia keeps and your net."}
                </p>
              </div>
            </div>
          )}

          {/* Step 2 — CLABE */}
          {step === 2 && (
            <div>
              <StepHeader
                n={2} total={3}
                icon={Landmark}
                title={es ? 'Cuenta bancaria (CLABE)' : 'Bank account (CLABE)'}
                subtitle={es ? 'A dónde se transfieren tus anticipos' : 'Where your deposits are transferred'}
                language={language}
              />
              {alreadyHasClabe ? (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/70 p-3 flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">
                      {es ? 'Ya tienes una CLABE registrada' : 'CLABE already on file'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {es ? 'Termina en' : 'Ends in'} <span className="font-mono">•••• {paymentModeState.clabe_last4}</span>.
                      {' '}
                      {es
                        ? 'Puedes cambiarla desde Configuración cuando quieras.'
                        : 'You can change it from Settings anytime.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="wizard-clabe">
                    {es ? 'CLABE (18 dígitos)' : 'CLABE (18 digits)'}
                  </Label>
                  <Input
                    id="wizard-clabe"
                    type="text"
                    inputMode="numeric"
                    placeholder="002180001234567890"
                    value={clabe}
                    onChange={(e) => setClabe(e.target.value.replace(/\D/g, '').slice(0, 18))}
                    className="h-12 font-mono tracking-wider"
                    maxLength={18}
                    data-testid="wizard-clabe-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {es
                      ? 'La CLABE es tu número de cuenta interbancario. Puedes verlo en tu app bancaria o en un estado de cuenta.'
                      : "CLABE is your interbank account number. Find it in your bank's app or on a statement."}
                  </p>
                  {clabe && clabe.length !== 18 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {clabe.length}/18 {es ? 'dígitos' : 'digits'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3 — accept terms */}
          {step === 3 && (
            <div>
              <StepHeader
                n={3} total={3}
                icon={FileCheck2}
                title={es ? 'Términos y Comisiones' : 'Terms & Fees'}
                subtitle={es ? 'Acepta el acuerdo de comisiones vigente' : 'Accept the current fee agreement'}
                language={language}
              />
              {alreadyAcceptedTerms && !termsPayload ? (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/70 p-3 flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">
                      {es ? 'Términos ya aceptados' : 'Terms already accepted'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {es
                        ? `Aceptaste la versión ${paymentModeState.commission_terms_version || 'vigente'} al registrarte. No necesitas volver a firmar.`
                        : `You accepted version ${paymentModeState.commission_terms_version || 'current'} at registration. No need to re-sign.`}
                    </p>
                  </div>
                </div>
              ) : termsPayload ? (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/70 p-3 flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">{es ? '¡Aceptado!' : 'Accepted!'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono break-all">
                      v{termsPayload.version} · {String(termsPayload.hash || '').slice(0, 16)}...
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 p-3 text-sm text-amber-900 dark:text-amber-200">
                    <p className="font-medium mb-1">
                      {es ? '¿Por qué necesito firmar esto?' : 'Why do I need to sign this?'}
                    </p>
                    <p className="text-xs leading-relaxed">
                      {es
                        ? 'Como vas a recibir pagos en línea, la ley requiere que aceptes claramente las comisiones y condiciones. Es un requisito legal (y también protege tu negocio).'
                        : "Since you'll receive online payments, the law requires you to explicitly accept fees and conditions. It's a legal requirement (and protects your business too)."}
                    </p>
                  </div>
                  <Button
                    type="button"
                    className="w-full h-11 btn-coral mt-3"
                    onClick={openTermsModal}
                    data-testid="wizard-open-terms-btn"
                  >
                    <FileCheck2 className="h-4 w-4 mr-2" />
                    {es ? 'Leer y aceptar' : 'Read and accept'}
                  </Button>
                </>
              )}

              {/* Final summary of what we're about to submit */}
              <div className="mt-4 rounded-lg border border-border/70 p-3 space-y-1.5">
                <p className="text-xs font-semibold">{es ? 'Al confirmar:' : 'On confirm:'}</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    {es ? 'Se activa el cobro de anticipos.' : 'Online deposits are enabled.'}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    {es
                      ? 'Si Stripe Connect no está listo, te llevaremos a completarlo.'
                      : "If Stripe Connect isn't ready, we'll take you there."}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <AlertCircle className="h-3 w-3 text-amber-600" />
                    {es
                      ? 'Solo podrás cambiar la modalidad después de 30 días.'
                      : 'You can only switch back after 30 days.'}
                  </li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter className="flex-row justify-between gap-2 mt-2">
            {step === 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="wizard-cancel-btn"
              >
                {es ? 'Cancelar' : 'Cancel'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={goBack}
                disabled={submitting}
                data-testid="wizard-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {es ? 'Atrás' : 'Back'}
              </Button>
            )}

            {step < 3 ? (
              <Button
                type="button"
                className="btn-coral"
                onClick={step === 2 ? handleClabeContinue : goNext}
                data-testid="wizard-next-btn"
              >
                {es ? 'Continuar' : 'Continue'}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                className="btn-coral"
                onClick={handleSubmit}
                disabled={submitting || (!alreadyAcceptedTerms && !termsPayload)}
                data-testid="wizard-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Sparkles className="h-4 w-4 mr-1" />{es ? 'Activar anticipos' : 'Enable deposits'}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calculator + Terms modal (reused from registration flow) */}
      <CommissionBreakdownModal
        open={showTermsModal}
        onOpenChange={setShowTermsModal}
        language={language}
        initialAmount={500}
        onAccept={handleTermsAccepted}
      />
    </>
  );
}
